import { PhoneConfigOrg } from '@wetags/phone-config';
import { DevicesApi } from '@frontend/api-devices';
import { GlobalBannerDynamic } from '@frontend/global-info-banner';
import { useTranslation } from '@frontend/i18n';
import { useHasPhoneSettingsAccess, usePhoneSettingsShallowStore } from '@frontend/phone-config';
import { useQuery } from '@frontend/react-query-helpers';
import { useAppScopeStore } from '@frontend/scope';
import { useSettingsNavigate } from '@frontend/settings-routing';
import { useModalControl } from '@frontend/design-system';
import { queryKeys } from '../query-keys';
import { trackingId } from '../tracking';
import { AssignE911AddressModal } from './assign-e911-address-modal';

// Devices are considered active if they registered within the last 5 days.
const ACTIVE_DEVICE_WINDOW_MS = 5 * 24 * 60 * 60 * 1000;

export const E911MissingAddressesGlobalBanner = () => {
  const { t } = useTranslation('phone', { keyPrefix: 'e911' });
  const [isE911EnforcementEnabled] = PhoneConfigOrg.useWetag('e911-enforcement');
  const { selectedLocationIds, accessibleLocationData } = useAppScopeStore();
  const { settingsTenantLocation } = usePhoneSettingsShallowStore('settingsTenantLocation');
  const tenantId = settingsTenantLocation?.phoneTenantId ?? '';
  const phoneAccess = useHasPhoneSettingsAccess({
    selectedLocations: selectedLocationIds?.map((id) => accessibleLocationData[id]).filter(Boolean) ?? [],
  });
  const assignModalControl = useModalControl();
  const { navigate } = useSettingsNavigate();

  const { data: unassignedE911Devices = [], isLoading } = useQuery({
    queryKey: [...queryKeys.settings.listUnassignedE911Devices(tenantId), selectedLocationIds],
    queryFn: () =>
      DevicesApi.ListUnassignedE911Devices({
        locationIds: selectedLocationIds,
        ...(tenantId ? { tenantId } : {}),
      }),
    enabled: isE911EnforcementEnabled && phoneAccess.hasFullPhoneAccess,
    staleTime: 0,
    select: (data) => data?.unassignedDevices ?? [],
    meta: {
      error: {
        message: 'E911MissingAddressesGlobalBanner:useQuery | Failed to fetch unassigned E911 devices',
        data: { tenantId, selectedLocationIds },
      },
    },
  });

  const now = Date.now();
  const unassignedActiveDevices = unassignedE911Devices.filter((device) => {
    const lastRegisteredAt = Date.parse(device.lastRegisteredAt ?? '');
    return Number.isFinite(lastRegisteredAt) && now - lastRegisteredAt < ACTIVE_DEVICE_WINDOW_MS;
  });
  const unassignedDeviceCount = unassignedActiveDevices.length;

  const showBanner = phoneAccess.hasE911AddressesAccess && !isLoading && unassignedDeviceCount > 0;

  if (!phoneAccess.hasE911AddressesAccess && !assignModalControl.modalProps.show) {
    return null;
  }

  const handleAssignE911Addresses = () => {
    navigate({ to: '/phone/e911', search: { tab: 'device-view' } });
    assignModalControl.openModal();
  };

  return (
    <>
      {showBanner && (
        <GlobalBannerDynamic
          id='e911-missing-addresses'
          type='error'
          isDismissible={false}
          title={t('{{count}} Active Devices are Missing E911 Addresses', { count: unassignedDeviceCount })}
          message={t(
            'Assigning E911 addresses is a legal requirement that will allow emergency callers to quickly get assistance. It is critical that you assign E911 addresses as soon as possible to keep your practice and clients safe.',
            { count: unassignedDeviceCount }
          )}
          action={{
            label: t('Assign E911 Addresses'),
            onClick: handleAssignE911Addresses,
          }}
          trackingId={trackingId({
            context: 'setting',
            feature: 'e911',
            details: 'assign-e911-addresses-global-banner',
          })}
        />
      )}
      <AssignE911AddressModal {...assignModalControl.modalProps} unassignedActiveDevices={unassignedActiveDevices} />
    </>
  );
};
